import React, { useMemo, useState } from "react";

import { Button, Popover, Stack } from "@mui/material";
import { Dropdown } from "components/Tagger/General/Dropdown";
import { useMeasureParametersSelector } from "store";

type MeasureOption = { value: number; label: string };

export type MetadataErrors = {
  effectiveDate?: string;
  currency?: string;
  investor?: string;
};

interface Props {
  anchorEl: HTMLElement;
  value: number;
  preselectedMeasure?: { id: number; name: string } | null;
  onApply: (measureId: number, value: number) => void;
  onCancel: () => void;
}

export const PolygonParameterPopover: React.FC<Props> = ({
  anchorEl,
  value,
  preselectedMeasure,
  onApply,
  onCancel
}) => {
  const measureParameters = useMeasureParametersSelector();

  const anchorPosition = useMemo(() => {
    const rect = anchorEl.getBoundingClientRect();
    return { top: rect.bottom + 4, left: rect.left };
  }, [anchorEl]);

  const measureOptions = useMemo<MeasureOption[]>(
    () => measureParameters.map((m) => ({ value: m.id, label: m.name })),
    [measureParameters]
  );

  const [selectedMeasure, setSelectedMeasure] = useState<MeasureOption | null>(
    preselectedMeasure ? { value: preselectedMeasure.id, label: preselectedMeasure.name } : null
  );

  const canApply = !!selectedMeasure && value != null;

  return (
    <Popover
      open
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
      onClose={onCancel}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: {
          sx: {
            borderRadius: "8px",
            boxShadow: "-2px 2px 15px 0px rgba(0,0,0,0.25)",
            width: 320,
            p: "24px",
            zIndex: 1300
          }
        }
      }}
    >
      <Stack gap="16px">
        <Dropdown
          label="Parameter"
          placeholder="Choose Parameter"
          options={measureOptions}
          value={selectedMeasure}
          onChange={(_, option) => setSelectedMeasure(option ?? null)}
        />

        <Stack direction="row" justifyContent="flex-end" gap="8px">
          <Button
            onClick={onCancel}
            sx={{
              borderRadius: "32px",
              border: "1px solid #7b92a9",
              color: "#071f35",
              textTransform: "none",
              padding: "4px 16px",
              fontSize: "16px",
              fontWeight: 400,
              lineHeight: "24px"
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => selectedMeasure && value != null && onApply(selectedMeasure.value, value)}
            disabled={!canApply}
            sx={{
              borderRadius: "32px",
              bgcolor: "#071f35",
              color: "white",
              textTransform: "none",
              padding: "4px 16px",
              fontSize: "16px",
              fontWeight: 400,
              lineHeight: "24px",
              "&:hover": { bgcolor: "#0a2a45" },
              "&:disabled": { bgcolor: "#bac7ce", color: "white" }
            }}
          >
            Apply
          </Button>
        </Stack>
      </Stack>
    </Popover>
  );
};
